/**
 * usePanelData - Panel Data Loading Hook
 * 
 * Extracts panel-specific data loading from PagePanel.tsx:
 * - Reference library documents
 * - Projects
 * - Company documents
 * - Document control (design docs)
 * - Quality metrics
 * - Suppliers
 * - Crosswalk data
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  referenceDocumentsApi, 
  projectsApi, 
  companyDocumentsApi,
  qmsApi,
  suppliersApi,
  crosswalkApi,
  designDocumentsApi,
} from '../services/api';
import { logger } from '../utils/logger';
import type { 
  PagePanelId, 
  ReferenceDocumentCard, 
  RecentDocumentGroup,
  ReferenceScopeType,
} from '../types/panel-types';
import type { ProjectInstance, CompanyDocument, UsageMetrics, DocumentAccessLog, DesignDocument, Supplier, PurchaseOrder } from '../types';
import { SCOPE_TYPE_INFO } from '../types/panel-types';

interface UsePanelDataProps {
  panelType: PagePanelId;
}

interface UsePanelDataReturn {
  // Reference Library
  recentGroups: RecentDocumentGroup[];
  setRecentGroups: React.Dispatch<React.SetStateAction<RecentDocumentGroup[]>>;
  loadingRecent: boolean;
  loadRecentDocuments: () => Promise<void>;
  
  // Projects
  projects: ProjectInstance[];
  setProjects: React.Dispatch<React.SetStateAction<ProjectInstance[]>>;
  loadingProjects: boolean;
  selectedProject: ProjectInstance | null;
  setSelectedProject: (project: ProjectInstance | null) => void;
  editingProject: ProjectInstance | null;
  setEditingProject: (project: ProjectInstance | null) => void;
  loadProjects: () => Promise<void>;
  
  // Company Documents
  companyDocs: CompanyDocument[];
  loadingCompanyDocs: boolean;
  loadCompanyDocs: () => Promise<void>;
  
  // Document Control
  designDocs: DesignDocument[];
  loadingDocControl: boolean;
  loadDocControlData: () => Promise<void>;
  
  // Quality
  usageMetrics: UsageMetrics[];
  accessLogs: DocumentAccessLog[];
  loadingQuality: boolean;
  loadQualityData: () => Promise<void>;
  
  // Suppliers
  suppliers: Supplier[];
  avl: PurchaseOrder[];
  loadingSuppliers: boolean;
  loadSuppliersData: () => Promise<void>;
  
  // Crosswalk
  crosswalkEntries: unknown[];
  crosswalkStandards: unknown[];
  crosswalkFilterStandard: string;
  setCrosswalkFilterStandard: (standard: string) => void;
  crosswalkFilterClause: string;
  setCrosswalkFilterClause: (clause: string) => void;
  loadingCrosswalk: boolean;
  loadCrosswalkData: () => Promise<void>;
  
  // Error state
  error: string | null;
  setError: (error: string | null) => void;
  
  // Load all data for current panel
  loadPanelData: () => Promise<void>;
}

export function usePanelData({ panelType }: UsePanelDataProps): UsePanelDataReturn {
  // Reference Library state
  const [recentGroups, setRecentGroups] = useState<RecentDocumentGroup[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  
  // Projects state
  const [projects, setProjects] = useState<ProjectInstance[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState<ProjectInstance | null>(null);
  const [editingProject, setEditingProject] = useState<ProjectInstance | null>(null);
  
  // Company Documents state
  const [companyDocs, setCompanyDocs] = useState<CompanyDocument[]>([]);
  const [loadingCompanyDocs, setLoadingCompanyDocs] = useState(true);
  
  // Document Control state
  const [designDocs, setDesignDocs] = useState<DesignDocument[]>([]);
  const [loadingDocControl, setLoadingDocControl] = useState(true);
  
  // Quality state
  const [usageMetrics, setUsageMetrics] = useState<UsageMetrics[]>([]);
  const [accessLogs, setAccessLogs] = useState<DocumentAccessLog[]>([]);
  const [loadingQuality, setLoadingQuality] = useState(true);
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [avl, setAVL] = useState<PurchaseOrder[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  
  // Crosswalk state
  const [crosswalkEntries, setCrosswalkEntries] = useState<unknown[]>([]);
  const [crosswalkStandards, setCrosswalkStandards] = useState<unknown[]>([]);
  const [crosswalkFilterStandard, setCrosswalkFilterStandard] = useState('');
  const [crosswalkFilterClause, setCrosswalkFilterClause] = useState('');
  const [loadingCrosswalk, setLoadingCrosswalk] = useState(true);
  
  // Error state
  const [error, setError] = useState<string | null>(null);

  /**
   * Load reference library documents
   */
  const loadRecentDocuments = useCallback(async () => {
    setLoadingRecent(true);
    setError(null);
    
    try {
      const recentKey = `recent_${panelType}`;
      const savedRecent = localStorage.getItem(recentKey);
      const recentIds = savedRecent ? JSON.parse(savedRecent) : [];
      
      // Only fetch Reference Library documents (scope=all-projects means assignedProjects is empty)
      const allDocs = await referenceDocumentsApi.list({ scope: 'all-projects' });
      const docs = Array.isArray(allDocs) ? allDocs : (allDocs?.documents || []);
      
      // Valid reference scope types (external technical references only)
      const validScopeTypes: ReferenceScopeType[] = [
        'industry-standard', 'advisory-circular', 'guidance', 'specification'
      ];
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ReferenceDocument type not fully defined
      const cards: ReferenceDocumentCard[] = docs.map((doc: Record<string, unknown>) => ({
        documentId: doc.documentId,
        title: doc.title,
        documentType: validScopeTypes.includes(doc.documentType) 
          ? doc.documentType 
          : 'company-reference',
        standardNumber: doc.standardNumber,
        description: doc.description,
        aiSummary: doc.aiSummary,
        summaryGeneratedAt: doc.summaryGeneratedAt,
        indexStatus: doc.indexStatus,
        chunkCount: doc.chunkCount,
        organization: doc.organization,
        version: doc.version,
        publishDate: doc.publishDate,
        createdAt: doc.createdAt,
        lastAccessedAt: doc.lastAccessedAt,
        relatedDocuments: doc.relatedDocuments,
      }));
      
      const recentSet = new Set(recentIds);
      cards.forEach(card => {
        if (recentSet.has(card.documentId)) {
          card.lastAccessedAt = new Date().toISOString();
        }
      });
      
      cards.sort((a, b) => {
        if (a.lastAccessedAt && b.lastAccessedAt) {
          return new Date(b.lastAccessedAt).getTime() - new Date(a.lastAccessedAt).getTime();
        }
        if (a.lastAccessedAt) return -1;
        if (b.lastAccessedAt) return 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      
      const allScopeTypes: ReferenceScopeType[] = [
        'industry-standard', 'advisory-circular', 'guidance', 'specification'
      ];
      
      // Don't slice here - let ReferenceLibraryContent handle display limiting
      // This allows the "Show all" button to appear when there are more than 5 docs
      const groups: RecentDocumentGroup[] = allScopeTypes.map(scopeType => ({
        scopeType,
        label: SCOPE_TYPE_INFO[scopeType].label,
        icon: SCOPE_TYPE_INFO[scopeType].icon,
        documents: cards.filter(c => c.documentType === scopeType),
        isExpanded: true,
      })).filter(g => g.documents.length > 0);
      
      setRecentGroups(groups);
    } catch (err: unknown) {
      logger.error('Failed to load recent documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoadingRecent(false);
    }
  }, [panelType]);

  /**
   * Load projects
   */
  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const data = await projectsApi.listMyProjects();
      setProjects(data);
    } catch (err: unknown) {
      logger.error('Failed to load projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  /**
   * Load company documents
   */
  const loadCompanyDocs = useCallback(async () => {
    setLoadingCompanyDocs(true);
    try {
      const docs = await companyDocumentsApi.list();
      setCompanyDocs(docs);
    } catch (err: unknown) {
      logger.error('Failed to load company documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoadingCompanyDocs(false);
    }
  }, []);

  /**
   * Load document control data
   */
  const loadDocControlData = useCallback(async () => {
    setLoadingDocControl(true);
    try {
      const [projectsData, designDocsData, companyDocsData] = await Promise.all([
        projectsApi.listMyProjects(),
        designDocumentsApi.list(),
        companyDocumentsApi.list(),
      ]);
      
      setProjects(projectsData);
      setDesignDocs(designDocsData);
      setCompanyDocs(companyDocsData);
    } catch (err: unknown) {
      logger.error('Failed to load document control data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoadingDocControl(false);
    }
  }, []);

  /**
   * Load quality data
   */
  const loadQualityData = useCallback(async () => {
    setLoadingQuality(true);
    try {
      const [metricsData, logsData] = await Promise.all([
        qmsApi.getMetrics(),
        qmsApi.getAccessLogs(),
      ]);
      setUsageMetrics(metricsData);
      setAccessLogs(logsData);
    } catch (err: unknown) {
      logger.error('Failed to load quality data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load quality data');
    } finally {
      setLoadingQuality(false);
    }
  }, []);

  /**
   * Load suppliers data
   */
  const loadSuppliersData = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      const [suppliersData, avlData] = await Promise.all([
        suppliersApi.list(),
        suppliersApi.getAVL(),
      ]);
      setSuppliers(suppliersData);
      setAVL(avlData);
    } catch (err: unknown) {
      logger.error('Failed to load suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  /**
   * Load crosswalk data
   */
  const loadCrosswalkData = useCallback(async () => {
    setLoadingCrosswalk(true);
    try {
      const [entriesData, standardsData] = await Promise.all([
        crosswalkApi.getEntries(crosswalkFilterStandard, crosswalkFilterClause),
        crosswalkApi.getStandards(),
      ]);
      setCrosswalkEntries(entriesData);
      setCrosswalkStandards(standardsData);
    } catch (err: unknown) {
      logger.error('Failed to load crosswalk:', err);
      setError(err instanceof Error ? err.message : 'Failed to load crosswalk');
    } finally {
      setLoadingCrosswalk(false);
    }
  }, [crosswalkFilterStandard, crosswalkFilterClause]);

  /**
   * Load data based on panel type
   */
  const loadPanelData = useCallback(async () => {
    setError(null);
    
    switch (panelType) {
      case 'reference-library':
        await loadRecentDocuments();
        break;
      case 'projects':
        await loadProjects();
        break;
      case 'company-docs':
        await loadCompanyDocs();
        break;
      case 'quality':
        await loadQualityData();
        break;
      case 'suppliers':
        await loadSuppliersData();
        break;
      case 'crosswalk':
        await loadCrosswalkData();
        break;
      case 'validation':
        setLoadingRecent(false);
        break;
      case 'doc-control':
        await loadDocControlData();
        break;
      default:
        setLoadingRecent(false);
    }
  }, [panelType, loadRecentDocuments, loadProjects, loadCompanyDocs, loadQualityData, loadSuppliersData, loadCrosswalkData, loadDocControlData]);

  // Load data when panel type changes
  useEffect(() => {
    setSelectedProject(null);
    loadPanelData();
  }, [panelType, loadPanelData]);

  return {
    // Reference Library
    recentGroups,
    setRecentGroups,
    loadingRecent,
    loadRecentDocuments,
    
    // Projects
    projects,
    setProjects,
    loadingProjects,
    selectedProject,
    setSelectedProject,
    editingProject,
    setEditingProject,
    loadProjects,
    
    // Company Documents
    companyDocs,
    loadingCompanyDocs,
    loadCompanyDocs,
    
    // Document Control
    designDocs,
    loadingDocControl,
    loadDocControlData,
    
    // Quality
    usageMetrics,
    accessLogs,
    loadingQuality,
    loadQualityData,
    
    // Suppliers
    suppliers,
    avl,
    loadingSuppliers,
    loadSuppliersData,
    
    // Crosswalk
    crosswalkEntries,
    crosswalkStandards,
    crosswalkFilterStandard,
    setCrosswalkFilterStandard,
    crosswalkFilterClause,
    setCrosswalkFilterClause,
    loadingCrosswalk,
    loadCrosswalkData,
    
    // Error
    error,
    setError,
    
    // Load all
    loadPanelData,
  };
}
